/**
 * Errores tipados del módulo de audio. Mismo patrón que `src/errors.ts`:
 * `Error` puros discriminados por `instanceof` y por el campo `code`. El
 * caller (apps/web en F1) decide cómo presentar al usuario o reintentar
 * — `@alter/api-client` nunca lo hace por sí solo.
 */

export type AudioRecorderErrorCode =
  /** El MediaStream del caller no está activo (track stopped/ended). */
  | 'stream_inactive'
  /** Lazy import del polyfill `opus-recorder` falló (Safari sin paquete). */
  | 'polyfill_load_failed'
  /** El encoder lanzó error durante la grabación o el flush final. */
  | 'encoding_failed';

export class AudioRecorderError extends Error {
  override readonly cause: unknown;
  constructor(
    readonly code: AudioRecorderErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.name = 'AudioRecorderError';
    this.cause = cause;
  }
}

export type AudioPlayerErrorCode =
  /** `mime_type` que el reproductor no maneja (ver `puedeReproducirMimeType`). */
  | 'unsupported_format'
  /** `decodeAudioData` falló (binario corrupto o codec no soportado). */
  | 'decode_failed'
  /** `HTMLAudioElement.play()` o `AudioBufferSourceNode.start()` falló. */
  | 'playback_failed';

export type AudioDeclaradoErrorCode =
  /** El cliente no puede reproducir el `downlink.mime_type` declarado. */
  | 'downlink_no_soportado'
  /** El cliente no puede grabar en el `uplink.mime_type` declarado. */
  | 'uplink_no_soportado';

/**
 * El bloque `audio` del Paso 2 declara un formato que este cliente no
 * puede cumplir (JC §0.2.1). El servidor declara y no negocia: el cliente
 * aborta **antes** de pedir el ticket, para no dejar registrado un intento
 * fallido del alumno.
 */
export class AudioDeclaradoError extends Error {
  constructor(
    readonly code: AudioDeclaradoErrorCode,
    readonly mime_type: string,
  ) {
    super(`Audio declarado no soportado (${code}): ${mime_type}`);
    this.name = 'AudioDeclaradoError';
  }
}

export class AudioPlayerError extends Error {
  override readonly cause: unknown;
  constructor(
    readonly code: AudioPlayerErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.name = 'AudioPlayerError';
    this.cause = cause;
  }
}
